import React from "react";
import { Button, Card } from "antd";
import {
  UploadCloud,
  WandSparkles,
  Download,
  CheckCircle,
  Image,
  Package,
  IndianRupee,
  TrendingUp,
  Cpu,
  Zap,
  Star,
} from "lucide-react";
import WebsiteHeader from "../Components/WebsiteHeader";
import WebsiteFooter from "../Components/WebsiteFooter";

export default function HowItWorks() {

    const handleRedirect = (url) => {
        // Sends the user to the specified external website URL
        window.location.href = url;
    };
  return (
    <div className="bg-[#fbfaff] min-h-screen text-slate-900">
      <WebsiteHeader />

      <main className="max-w-[1200px] mx-auto px-4 py-12">
        <section className="text-center mb-12">
          <div className="inline-flex bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-bold text-sm">
            ✨ How It Works
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold mt-5 leading-tight">
            3 Simple Steps to <br />
            <span className="text-purple-600">
              Reduce Shipping Charges
            </span>
          </h1>

          <p className="text-slate-600 text-lg mt-5 max-w-2xl mx-auto">
            Our AI optimizes your product image to reduce package size and help
            you get the lowest possible shipping charge on Meesho.
          </p>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <StepCard
            no="01"
            icon={<UploadCloud size={42} />}
            title="Upload Product Image"
            desc="Upload a clear front side image of your product."
            points={[
              "Use front side image",
              "JPG / PNG up to 1MB",
            ]}
            bottom={
              <MiniImageBox
                label="Original Image"
                size="₹119"
              />
            }
          />

          <StepCard
            no="02"
            icon={<WandSparkles size={42} />}
            title="AI Optimizes"
            desc="Our AI creates multiple optimized versions and finds the lowest shipping charge."
            points={[
              "Multiple optimized versions",
              "AI finds lowest shipping rate",
              "Maintains image quality",
            ]}
            bottom={
              <div className="bg-purple-50 rounded-xl p-4 text-purple-700 font-bold flex items-center gap-3">
                <WandSparkles size={22} />
                AI Processing...
              </div>
            }
          />

          <StepCard
            no="03"
            icon={<Download size={42} />}
            title="Download & Save"
            desc="Download the optimized image and upload it on Meesho."
            points={[
              "Get lowest shipping charge",
              "Upload on Meesho",
              "Save more on every order",
            ]}
            bottom={
              <MiniImageBox
                label="Optimized Image"
                size="₹58"
                optimized
              />
            }
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          <Card className="rounded-3xl shadow-sm">
            <h2 className="text-2xl font-extrabold text-center">
              Behind the Scenes
            </h2>
            <p className="text-purple-600 text-center font-semibold">
              ShipLess AI technology in action
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 items-center text-center">
              <BehindCard
                title="Original Image"
                desc1="High dimension"
                desc2="More package size"
              />

              <div>
                <div className="w-24 h-24 mx-auto bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600">
                  <Cpu size={46} />
                </div>
                <h3 className="font-bold mt-4">AI Optimization</h3>
                <p className="text-slate-500 text-sm">
                  Smart compression <br />
                  Keeps quality, reduces size
                </p>
              </div>

              <BehindCard
                title="Optimized Image"
                desc1="Lower dimension"
                desc2="Lower package size"
                optimized
              />
            </div>

            <div className="mt-8 bg-purple-50 text-purple-700 rounded-xl p-4 text-center font-bold">
              <Zap size={18} className="inline mr-2" />
              Result: Lower Package Size = Lower Shipping Charge
            </div>
          </Card>

          <Card className="rounded-3xl shadow-sm bg-purple-50">
            <h2 className="text-2xl font-extrabold text-center">
              Why It Works?
            </h2>
            <p className="text-purple-600 text-center font-semibold mb-8">
              Lower package size means lower shipping charge on Meesho.
            </p>

            <WhyRow
              icon={<IndianRupee />}
              text="Meesho calculates shipping based on product image dimensions."
              color="green"
            />

            <WhyRow
              icon={<Image />}
              text="Our AI reduces image dimensions without affecting quality."
              color="purple"
            />

            <WhyRow
              icon={<Package />}
              text="Smaller dimensions = Smaller package size."
              color="orange"
            />

            <WhyRow
              icon={<TrendingUp />}
              text="Smaller package size = Lower shipping charge."
              color="green"
            />
          </Card>
        </section>

        <section className="mt-10 bg-white border border-purple-100 rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5 shadow-sm">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-full bg-purple-600 text-white flex items-center justify-center">
              <Star size={34} />
            </div>

            <div>
              <h2 className="text-xl font-extrabold m-0">
                Built by a Meesho Seller, for Meesho Sellers
              </h2>
              <p className="text-slate-600 m-0 mt-2">
                We understand your challenges and built ShipLess AI to help you
                save more and grow your business.
              </p>
            </div>
          </div>

          <Button
          onClick={() => handleRedirect('https://dashboard.shipless.in/')}
            type="primary"
            size="large"
            className="bg-purple-600 h-12 px-8 font-bold"
          >
            Get 30 Free Credits →
          </Button>
        </section>
      </main>

      <WebsiteFooter/>
    </div>
  );
}

function StepCard({ no, icon, title, desc, points, bottom }) {
  return (
    <Card className="rounded-3xl shadow-sm text-center relative h-full">
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
        {no}
      </div>

      <div className="mt-12">
        <div className="w-24 h-24 rounded-full bg-purple-100 text-purple-600 mx-auto flex items-center justify-center">
          {icon}
        </div>

        <h2 className="text-xl font-extrabold mt-5">{title}</h2>
        <p className="text-purple-600 font-semibold max-w-xs mx-auto">
          {desc}
        </p>

        <div className="border-t border-dashed my-6" />

        <div className="space-y-3 text-left max-w-xs mx-auto">
          {points.map((item) => (
            <div key={item} className="flex items-center gap-3 font-semibold">
              <CheckCircle size={18} className="text-purple-600" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="mt-6">{bottom}</div>
      </div>
    </Card>
  );
}

function MiniImageBox({ label, size, optimized }) {
  return (
    <div className="bg-white border rounded-xl p-3 flex items-center gap-4 text-left">
      <div
        className={`w-16 h-16 rounded-lg flex items-center justify-center text-3xl ${
          optimized ? "bg-green-50" : "bg-slate-100"
        }`}
      >
        👗
      </div>

      <div className="flex-1">
        <p className="font-bold m-0">Example</p>
        <p className="text-slate-600 m-0">{label}</p>
      </div>

      <span
        className={`px-3 py-2 rounded-lg font-bold ${
          optimized
            ? "bg-green-100 text-green-700"
            : "bg-purple-100 text-purple-700"
        }`}
      >
        {size}
      </span>
    </div>
  );
}

function BehindCard({ title, desc1, desc2, optimized }) {
  return (
    <div>
      <div
        className={`w-24 h-24 mx-auto rounded-2xl flex items-center justify-center text-5xl ${
          optimized ? "bg-green-50" : "bg-slate-100"
        }`}
      >
        👗
      </div>
      <h3 className="font-bold mt-4">{title}</h3>
      <p className="text-slate-500 text-sm">
        {desc1} <br />
        {desc2}
      </p>
    </div>
  );
}

function WhyRow({ icon, text, color }) {
  const colors = {
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    orange: "bg-orange-100 text-orange-600",
  };

  return (
    <div className="bg-white rounded-xl p-4 mb-4 flex items-center gap-4">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${colors[color]}`}
      >
        {icon}
      </div>
      <p className="font-semibold m-0">{text}</p>
    </div>
  );
}